// src/context/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import Toast, { toastKind } from "../components/Toast/Toast";

const ToastContext = createContext(null);

// At most this many toasts on screen; the oldest gives way.
const MAX_VISIBLE = 3;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);

    // Same API as before. A second toast now stacks instead of replacing the
    // first; the same message again (a double tap) restarts that toast rather
    // than stacking a duplicate.
    const showToast = useCallback(({ message, type = 'success', duration = 4000, details, dismissible = true }) => {
        if (!message) return;
        nextId.current += 1;
        const id = nextId.current;
        const kind = toastKind(type);
        setToasts((list) => [
            ...list.filter((t) => !(t.message === message && toastKind(t.type) === kind)),
            { id, message, type, duration, details, dismissible },
        ].slice(-MAX_VISIBLE));
    }, []);

    // With an id, dismisses that toast; without, clears them all.
    const hideToast = useCallback((id) => {
        setToasts((list) => (id == null ? [] : list.filter((t) => t.id !== id)));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, hideToast }}>
            {children}

            {/* One region at the root, above everything; see Toast.less. */}
            {toasts.length > 0 && (
                <div className="gc-toast-region" aria-label="Notifications">
                    {toasts.map((t) => (
                        <ToastItem key={t.id} toast={t} onHide={hideToast} />
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
}

// Stable onClose per toast, so each toast's own timer is not restarted by
// its neighbours re-rendering.
function ToastItem({ toast, onHide }) {
    const onClose = useCallback(() => onHide(toast.id), [onHide, toast.id]);
    return (
        <Toast
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            details={toast.details}
            dismissible={toast.dismissible}
            onClose={onClose}
        />
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);

    if (!ctx) {
        throw new Error("useToast must be used within ToastProvider");
    }

    return ctx;
}

/**
 * Safer variant for components that may render outside the provider —
 * useful for tests that mount a page in a bare Router without wiring
 * the full provider stack. Returns a no-op stub instead of throwing so
 * a test's "does the page render at all?" assertion doesn't fail with
 * an unrelated toast-provider error.
 *
 * Production callers inside the AppShell / AppRoutes tree still see
 * the real toast object.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalToast() {
    const ctx = useContext(ToastContext);
    return ctx || { showToast: () => {}, hideToast: () => {} };
}
